import React, { useMemo, useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { useConfiguration } from '../../context/configurationContext';

import { submitContactForm } from '../../util/api';
import { ensureCurrentUser } from '../../util/data';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  composeValidators,
  emailFormatValid,
  maxLength,
  minLength,
  required,
} from '../../util/validators';

import {
  Button,
  FieldSelect,
  FieldTextInput,
  Form,
  H1,
  H3,
  Page,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ContactUsPage.module.css';

const SUBJECT_OPTIONS = [
  'General Support',
  'Booking Issue',
  'Billing Question',
  'Safety Concern',
  'Account Help',
  'Other',
];

const MAX_NAME_LENGTH = 120;
const MAX_EMAIL_LENGTH = 255;
const MAX_MESSAGE_LENGTH = 5000;
const MIN_MESSAGE_LENGTH = 10;

const HoneypotField = () => (
  <Field
    name="website"
    render={({ input }) => (
      <div className={css.honeypot}>
        <label htmlFor="contactUs-website">Leave this field empty</label>
        <input {...input} id="contactUs-website" type="text" tabIndex="-1" autoComplete="off" />
      </div>
    )}
  />
);

/**
 * Public contact page for marketplace support requests.
 *
 * @param {Object} props Component props.
 * @param {Object} [props.currentUser] Current authenticated user.
 * @returns {JSX.Element}
 */
export const ContactUsPageComponent = props => {
  const { currentUser } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const [submitState, setSubmitState] = useState({ status: 'idle', error: null, nonce: 0 });

  const user = ensureCurrentUser(currentUser);
  const initialValues = useMemo(
    () => ({
      fullName: user?.attributes?.profile?.displayName || '',
      email: user?.attributes?.email || '',
      subject: '',
      message: '',
      website: '',
    }),
    [user]
  );

  const title = intl.formatMessage({ id: 'ContactUsPage.title' });
  const schemaTitle = intl.formatMessage(
    { id: 'ContactUsPage.schemaTitle' },
    { marketplaceName: config.marketplaceName }
  );
  const schemaDescription = intl.formatMessage({ id: 'ContactUsPage.schemaDescription' });

  const requiredMessage = intl.formatMessage({ id: 'ContactUsPage.validation.required' });
  const invalidEmailMessage = intl.formatMessage({ id: 'ContactUsPage.validation.email' });
  const nameTooLongMessage = intl.formatMessage(
    { id: 'ContactUsPage.validation.nameTooLong' },
    { maxLength: MAX_NAME_LENGTH }
  );
  const emailTooLongMessage = intl.formatMessage(
    { id: 'ContactUsPage.validation.emailTooLong' },
    { maxLength: MAX_EMAIL_LENGTH }
  );
  const messageTooShortMessage = intl.formatMessage(
    { id: 'ContactUsPage.validation.messageTooShort' },
    { minLength: MIN_MESSAGE_LENGTH }
  );
  const messageTooLongMessage = intl.formatMessage(
    { id: 'ContactUsPage.validation.messageTooLong' },
    { maxLength: MAX_MESSAGE_LENGTH }
  );

  const onSubmit = async (values, form) => {
    setSubmitState({ status: 'submitting', error: null, nonce: submitState.nonce });

    try {
      await submitContactForm(values);
      form.reset();
      setSubmitState(prev => ({
        status: 'success',
        error: null,
        nonce: prev.nonce + 1,
      }));
    } catch (error) {
      setSubmitState(prev => ({
        status: 'error',
        error:
          error?.message ||
          intl.formatMessage({ id: 'ContactUsPage.submitErrorFallback' }),
        nonce: prev.nonce,
      }));
    }
  };

  return (
    <Page title={schemaTitle} description={schemaDescription}>
      <TopbarContainer />
      <main className={css.pageShell}>
        <section className={css.heroSection}>
          <div className={css.heroContent}>
            <p className={css.eyebrow}>
              <FormattedMessage id="ContactUsPage.eyebrow" />
            </p>
            <H1 as="h1" className={css.heroTitle}>
              <FormattedMessage id="ContactUsPage.heading" />
            </H1>
            <p className={css.heroText}>
              <FormattedMessage id="ContactUsPage.description" />
            </p>
          </div>
        </section>

        <section className={css.contentSection}>
          <div className={css.contentGrid}>
            <div className={css.infoCard}>
              <H3 as="h2" className={css.infoHeading}>
                <FormattedMessage id="ContactUsPage.supportHeading" />
              </H3>
              <p className={css.infoText}>
                <FormattedMessage id="ContactUsPage.supportText" />
              </p>
              <ul className={css.infoList}>
                <li className={css.infoListItem}>
                  <FormattedMessage id="ContactUsPage.supportItemGeneral" />
                </li>
                <li className={css.infoListItem}>
                  <FormattedMessage id="ContactUsPage.supportItemBooking" />
                </li>
                <li className={css.infoListItem}>
                  <FormattedMessage id="ContactUsPage.supportItemSafety" />
                </li>
              </ul>
            </div>

            <div className={css.formCard}>
              <H3 as="h2" className={css.formHeading}>
                {title}
              </H3>

              {submitState.status === 'success' ? (
                <div className={css.successBanner} role="status" aria-live="polite">
                  <FormattedMessage id="ContactUsPage.submitSuccess" />
                </div>
              ) : null}

              {submitState.status === 'error' && submitState.error ? (
                <div className={css.errorBanner} role="alert">
                  {submitState.error}
                </div>
              ) : null}

              <FinalForm
                key={submitState.nonce}
                initialValues={initialValues}
                onSubmit={onSubmit}
                render={({ handleSubmit, submitting }) => {
                  const isSubmitting = submitting || submitState.status === 'submitting';

                  return (
                    <Form className={css.form} onSubmit={handleSubmit}>
                      <HoneypotField />

                      <FieldTextInput
                        id="contactUs-fullName"
                        name="fullName"
                        type="text"
                        label={intl.formatMessage({ id: 'ContactUsPage.fullNameLabel' })}
                        placeholder={intl.formatMessage({
                          id: 'ContactUsPage.fullNamePlaceholder',
                        })}
                        validate={composeValidators(
                          required(requiredMessage),
                          maxLength(nameTooLongMessage, MAX_NAME_LENGTH)
                        )}
                      />

                      <FieldTextInput
                        id="contactUs-email"
                        name="email"
                        type="email"
                        label={intl.formatMessage({ id: 'ContactUsPage.emailLabel' })}
                        placeholder={intl.formatMessage({
                          id: 'ContactUsPage.emailPlaceholder',
                        })}
                        validate={composeValidators(
                          required(requiredMessage),
                          emailFormatValid(invalidEmailMessage),
                          maxLength(emailTooLongMessage, MAX_EMAIL_LENGTH)
                        )}
                      />

                      <FieldSelect
                        id="contactUs-subject"
                        name="subject"
                        label={intl.formatMessage({ id: 'ContactUsPage.subjectLabel' })}
                        validate={required(requiredMessage)}
                      >
                        <option value="">
                          {intl.formatMessage({ id: 'ContactUsPage.subjectPlaceholder' })}
                        </option>
                        {SUBJECT_OPTIONS.map(option => (
                          <option key={option} value={option}>
                            {intl.formatMessage({ id: `ContactUsPage.subject.${option}` })}
                          </option>
                        ))}
                      </FieldSelect>

                      <FieldTextInput
                        id="contactUs-message"
                        name="message"
                        type="textarea"
                        label={intl.formatMessage({ id: 'ContactUsPage.messageLabel' })}
                        placeholder={intl.formatMessage({
                          id: 'ContactUsPage.messagePlaceholder',
                        })}
                        validate={composeValidators(
                          required(requiredMessage),
                          minLength(messageTooShortMessage, MIN_MESSAGE_LENGTH),
                          maxLength(messageTooLongMessage, MAX_MESSAGE_LENGTH)
                        )}
                      />

                      <div className={css.actions}>
                        <Button
                          type="submit"
                          className={css.submitButton}
                          disabled={isSubmitting}
                        >
                          <FormattedMessage
                            id={
                              isSubmitting
                                ? 'ContactUsPage.submitInProgress'
                                : 'ContactUsPage.submitButton'
                            }
                          />
                        </Button>
                      </div>
                    </Form>
                  );
                }}
              />
            </div>
          </div>
        </section>
      </main>
      <FooterContainer />
    </Page>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

const ContactUsPage = compose(connect(mapStateToProps))(ContactUsPageComponent);

export default ContactUsPage;
